import { Attachment } from './attachment.model';

/**
 * A trailing token short and plain enough to be a real extension rather than part of a name.
 *
 * At least one letter is required, so a numeric tail stays part of the name: legal documents are
 * routinely called `Exhibit 3.14` or `Report.2024`, and treating `.14` as an extension would quietly
 * shorten the name the rename dialog prefills with. Extensions carrying digits (`7z`, `mp4`) still
 * strip. Kept identical to `UploadedFileNameResolver.EXTENSION_TAIL` on the backend.
 */
const EXTENSION_TAIL = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{1,5}$/;

/** Suffix the backend appends to the description of every generated document. */
const GENERATED_DOCUMENT_DESCRIPTION = 'Generated Document';

/**
 * Characters `AttachmentDownloadNaming.sanitize` strips before a name reaches a zip or a disk:
 * path/shell-hostile punctuation plus control characters. Spaces and hyphens are legal and must
 * survive — "MAQ-110 - court" is a normal document name.
 */
// eslint-disable-next-line no-control-regex
const ILLEGAL_FILENAME_CHARS = /[/\\:*?"<>|\x00-\x1f]/g;

/** Keeps names clear of filesystem limits once a dedup suffix and extension are added. */
const MAX_BASE_LENGTH = 120;

/**
 * Mirror of `AttachmentDownloadNaming.sanitize`. Returns null when nothing usable survives.
 *
 * Display and download have to agree on the name, so the rules live in one place per side and are
 * kept deliberately identical rather than approximated.
 */
export function sanitizeFilename(name: string | null | undefined): string | null {
  if (name === null || name === undefined) {
    return null;
  }
  const cleaned = name
    .replace(ILLEGAL_FILENAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[. ]+/, '')
    .replace(/[. \t]+$/, '');
  if (!cleaned) {
    return null;
  }
  return cleaned.length > MAX_BASE_LENGTH ? cleaned.substring(0, MAX_BASE_LENGTH).trim() : cleaned;
}

/**
 * Mirror of `AttachmentDownloadNaming.templateNameFromDescription`: strips the trailing
 * " (Generated Document)" marker, leaving the template name.
 */
export function templateNameFromDescription(description: string | null | undefined): string | null {
  const trimmed = (description ?? '').replace(new RegExp(`\\s*\\(${GENERATED_DOCUMENT_DESCRIPTION}\\)$`), '').trim();
  return trimmed && trimmed !== GENERATED_DOCUMENT_DESCRIPTION ? trimmed : null;
}

/**
 * Drops a trailing extension, matching `UploadedFileNameResolver.fileStem` on the backend so the
 * rename dialog offers exactly what the server would store.
 *
 * A name that merely contains a dot — "123 Main St. Apt 2" — is left whole rather than truncated at
 * the dot.
 */
export function fileStem(filename: string | null | undefined): string {
  const trimmed = (filename ?? '').trim().replace(/[. ]+$/, '');
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0) {
    return trimmed;
  }
  return EXTENSION_TAIL.test(trimmed.substring(dot + 1)) ? trimmed.substring(0, dot) : trimmed;
}

/**
 * The extension [fileStem] would strip, lowercased, or '' when the name carries none.
 *
 * The complement of `fileStem` and deliberately built from the same rule, so the two can never
 * disagree about where a name ends and its extension begins.
 */
export function fileExtension(filename: string | null | undefined): string {
  const trimmed = (filename ?? '').trim().replace(/[. ]+$/, '');
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0) {
    return '';
  }
  const tail = trimmed.substring(dot + 1);
  return EXTENSION_TAIL.test(tail) ? tail.toLowerCase() : '';
}

/**
 * One sentence of rename provenance for a document, or null when it has never been renamed.
 *
 * Shared so every surface that can rename a document words this identically (EE-350 Req 3.6): a
 * renamed file's name is no longer evidence by itself, so what it arrived as has to travel with it.
 */
export function attachmentRenamedNote(attachment: Attachment | null | undefined): string | null {
  const history = attachment?.name_history;
  if (!history?.length) {
    return null;
  }
  const by = history[history.length - 1].renamed_by;
  const uploaded = attachment?.original_filename?.trim();
  const renamed = by ? `Renamed by ${by}.` : 'Renamed.';
  return uploaded ? `Uploaded as ${uploaded}. ${renamed}` : renamed;
}

/**
 * The file's format, for appending to a display name — from the first source that actually knows it.
 *
 * `original_format` is optional, so older rows carry the extension only inside a filename, and a
 * RENAMED legacy generated document (`MAQ-182.docx` with no other fields) carries it nowhere but in
 * its rename history: the stored name is now an extensionless "Signed Lease" and `MAQ-182.docx` has
 * moved to `name_history[0].previous_name`. Without that last hop the list shows "Signed Lease"
 * while the download still serves "Signed Lease.docx".
 *
 * Shared with the rename dialog so the name a user is offered and the name shown in lists cannot
 * disagree about the extension.
 */
export function attachmentFormat(attachment: Attachment | null | undefined): string {
  return (
    // authoritative_format first: once an in-browser form fill supersedes one rendition, that is the
    // format the document is actually served as. `original_format` stays a write-once record of what
    // it was CREATED as, so reading it here showed ".docx" for a document that now downloads as a
    // PDF. Mirrors the server's effectivePreferredFormat.
    (attachment?.authoritative_format ?? '').toLowerCase() ||
    (attachment?.original_format ?? '').toLowerCase() ||
    fileExtension(attachment?.original_filename) ||
    fileExtension(attachment?.generated_filename) ||
    fileExtension(attachment?.name_history?.[0]?.previous_name)
  );
}
